/**
 * Care schemas
 */

import { z } from 'zod';
import { pillarScoreSchema } from '../validators';

/**
 * Daily check-in schema
 */
export const dailyCheckinSchema = z.object({
  moodScore: z.number().int().min(1).max(5),
  notes: z.string().max(500).optional(),
});

/**
 * Checkpoint response schema
 */
export const checkpointResponseSchema = z.object({
  checkpointId: z.uuid(),

  // Pillar scores
  housingScore: pillarScoreSchema.optional(),
  schoolingScore: pillarScoreSchema.optional(),
  workScore: pillarScoreSchema.optional(),
  wellbeingScore: pillarScoreSchema.optional(),

  // Extended scores (Business)
  extendedScores: z
    .object({
      productivity: pillarScoreSchema.optional(),
      engagement: pillarScoreSchema.optional(),
      satisfaction: pillarScoreSchema.optional(),
      retentionRisk: pillarScoreSchema.optional(),
      teamIntegration: pillarScoreSchema.optional(),
      skillDevelopment: pillarScoreSchema.optional(),
      cultureFit: pillarScoreSchema.optional(),
      careerProgress: pillarScoreSchema.optional(),
    })
    .optional(),

  // Additional responses
  responses: z.record(z.string(), z.unknown()).optional(),
});

/**
 * A Care support request — the citizen asks the people supporting their move
 * for help on one pillar (CARE-SUP-01): what it is about, how urgent it is,
 * and what is happening, in their words. ONE contract: platform-api validates
 * the POST with it and the phone's form checks the same rule before sending.
 * Messages are i18n keys.
 */
export const CARE_SUPPORT_SEVERITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type CareSupportRequestSeverity = (typeof CARE_SUPPORT_SEVERITIES)[number];

export const CARE_SUPPORT_DESCRIPTION_MIN = 10;
export const CARE_SUPPORT_DESCRIPTION_MAX = 2000;

export const createCareSupportRequestSchema = z.object({
  pillarId: z.string().trim().min(1, 'care.supportRequest.validation.pillarRequired').max(50),
  severity: z.enum(CARE_SUPPORT_SEVERITIES),
  description: z
    .string()
    .trim()
    .min(CARE_SUPPORT_DESCRIPTION_MIN, 'care.supportRequest.validation.descriptionShort')
    .max(CARE_SUPPORT_DESCRIPTION_MAX, 'care.supportRequest.validation.descriptionLong'),
});

// Type exports
export type DailyCheckinInput = z.infer<typeof dailyCheckinSchema>;
export type CheckpointResponseInput = z.infer<typeof checkpointResponseSchema>;
export type CreateCareSupportRequestInput = z.infer<typeof createCareSupportRequestSchema>;
